import React, { useContext, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { LuPalette, LuCode, LuServer, LuGitBranch } from "react-icons/lu";
import { useNavigate } from "react-router";
import { Images } from "../controller/Images";
import { GlobalStateContext } from "../controller/GlobalState";
import { emailRegex, isValidPhone, workflowsteps, tech, company, emailaddress } from "../controller/global";
import { submitLead } from "../controller/leads";
import "../styles/how-we-work.css";

// Same trust-logo set shown on /generative-ai-development, /about, and
// /our-core-team.
const TRUST_LOGOS = [
   // { name: "Aptor", src: Images.aptor_logo },
  { name: "BoxERP", src: Images.boxErp },
  { name: "GE", src: Images.ge_logo },
  { name: "Jennison", src: Images.Jennison_Dark_logo },
  { name: "Regent", src: Images.regent_logo },
  { name: "ThreeLockBox", src: Images.threelockbox_logo },
  { name: "Stokkd", src: Images.stokkd_logo },
  { name: "Ujala Enterprises", src: Images.UjalaEnterprises_logo },
];

// Cost and timeline. Every line below is condensed from pages this site
// already publishes — the four drivers and the pilot/production split from
// /agentic-ai-cost, the response time and the seven stages from this page.
// No figure has been invented, because the company's published position is
// that quoting one before seeing the systems is guessing.
const COST_DRIVERS = [
  { t: "System integrations", d: "The number and complexity of integrations affect development effort. API availability, legacy systems and data formats all influence the estimate." },
  { t: "Security and operational risk", d: "Workflows involving payments, approvals or sensitive records require appropriate access controls, audit trails and review processes. These requirements shape the architecture and scope." },
  { t: "Data readiness", d: "Data quality, consistency and accessibility affect implementation. Cleaning records, organizing documents and reconciling systems may be part of the project." },
  { t: "Testing and validation", d: "Testing requirements depend on the application and its operating environment. Regulated or business-critical workflows may require additional validation and ongoing monitoring." },
];

const TIMELINES = [
  { k: "A reply", v: "We aim to respond to project inquiries within one business day." },
  { k: "A written estimate", v: "Following an assessment of your requirements, with scope, costs, milestones and key risks documented." },
  { k: "A pilot or MVP", v: "A focused initial release, with the timeline agreed around core features, dependencies and testing needs." },
  { k: "Production", v: "Delivery planned around application complexity, integrations, security requirements and launch readiness." },
  { k: "After launch", v: "Maintenance, updates and enhancements based on an agreed support scope." },
];

const APPROACH = [
  { num: "01 / PLANNING", title: "Start with a shared understanding.", copy: "We clarify your goals, users, existing systems and constraints before defining the scope. An agreed plan gives your team a clear view of priorities, dependencies and expected deliverables.", tags: ["Business requirements", "Defined scope", "Delivery milestones"] },
  { num: "02 / COLLABORATION", title: "Make decisions together.", copy: "Design reviews and delivery checkpoints give your stakeholders opportunities to provide feedback. We discuss changes and their impact on scope, budget and timing before moving forward.", tags: ["Design reviews", "Progress updates", "Change planning"] },
  { num: "03 / RESPONSIBILITY", title: "Keep responsibilities clear.", copy: "Each stage has defined activities, deliverables and review expectations. We coordinate the work across design, development and testing, with launch and support requirements included in the plan.", tags: ["Defined deliverables", "Review checkpoints", "Launch readiness"] },
];

const PHASE_GROUPS = [
  { n: "01", t: "Discovery & Planning", d: "Understand your business requirements, define the scope and map user journeys, architecture and delivery priorities." },
  { n: "02", t: "Design & Content", d: "Review interface designs and prepare the copy, assets and information needed to make the product usable." },
  { n: "03", t: "Development & Testing", d: "Build the agreed features and integrations, then validate functionality, usability and performance against the requirements." },
  { n: "04", t: "Launch & Support", d: "Prepare the production release, coordinate handover and maintain the application through an agreed support scope." },
];

const findTech = (key) => tech.find((t) => t.key === key);
const TOOL_CARDS = [
  { Icon: LuPalette, title: "Design & Prototyping", copy: `Our design toolkit includes ${findTech("design").children.map((c) => c.name).join(", ")}.`, featured: true },
  { Icon: LuCode, title: "Front-End Development", copy: `Available technologies include ${findTech("front-end").children.slice(0, 6).map((c) => c.name).join(", ")}.` },
  { Icon: LuServer, title: "Back-End & Data", copy: `Available technologies include ${[...findTech("back-end").children.slice(0, 3), ...findTech("database").children.slice(0, 3)].map((c) => c.name).join(", ")}.` },
  { Icon: LuGitBranch, title: "Deployment & Operations", copy: `Our deployment toolkit includes ${findTech("devops").children.map((c) => c.name).join(", ")}.` },
];

const PROCESS_CRITERIA = [
  { a: "Requirements and priorities agreed", b: "SCOPE" },
  { a: "Stakeholder reviews planned", b: "FEEDBACK" },
  { a: "Testing aligned with requirements", b: "QUALITY" },
  { a: "Launch and support responsibilities defined", b: "HANDOVER" },
];

const FACTS = [
  { a: "07", b: "Delivery stages" },
  { a: "4", b: "Connected phases" },
  { a: "3", b: "Collaboration principles" },
  { a: "1", b: "Coordinated delivery plan" },
];

const APPLIES_TO = [
  { a: "Web Development", b: "User journeys, integrations and browser testing", path: "/web-development" },
  { a: "Mobile App Development", b: "App flows, device testing and release planning", path: "/mobile-app-development" },
  { a: "Cloud & DevOps", b: "Infrastructure, deployment and monitoring", path: "/devops-services" },
  { a: "AI Integration", b: "Data readiness, evaluation and human review", path: "/ai-integration-services" },
];

const WHERE_IT_APPLIES = [
  { a: "Web Development", b: "Plan user journeys and integrations, review interfaces and test the application across relevant browsers." },
  { a: "Mobile App Development", b: "Define platform requirements, validate app flows and prepare testing and distribution for iOS and Android." },
  { a: "UI/UX Design", b: "Explore user needs, develop wireframes and prototypes, and refine designs through stakeholder feedback." },
  { a: "Cloud & DevOps", b: "Assess infrastructure needs, automate deployments and plan monitoring, access controls and operational handover." },
  { a: "Digital Transformation", b: "Map current workflows, prioritize improvements and plan integrations or modernization in manageable phases." },
  { a: "Cybersecurity & Compliance", b: "Agree the assessment scope, identify risks and document remediation priorities and applicable requirements." },
  { a: "Cloud Development", b: "Design cloud applications around integration needs, operating costs, security and expected usage." },
  { a: "Product Support & Maintenance", b: "Define issue reporting, maintenance activities and improvement priorities within an agreed support scope." },
];

const FAQS = [
  { q: "What are the stages of your software development process?", a: "Our process covers requirements discovery, planning, interface design, content preparation, development, testing and launch, and maintenance. Activities and deliverables are tailored to the project scope." },
  { q: "How do you adapt the process for an MVP or smaller project?", a: "We focus the scope on essential features while planning design, development, testing and launch readiness. The depth of each activity depends on the project, and some activities may run alongside one another." },
  { q: "How will our team be involved?", a: "Your team helps confirm requirements, review designs and evaluate completed work at agreed checkpoints. We discuss feedback, outstanding decisions and proposed scope changes throughout delivery." },
  { q: "How do you estimate project timelines?", a: "We assess the feature scope, integrations, available content and data, testing requirements and external dependencies. The delivery plan sets out milestones, responsibilities and assumptions, with changes discussed as the project develops." },
  { q: "What happens after launch?", a: "Post-launch work can include maintenance, issue resolution, security updates and enhancements. Support coverage, responsibilities and response expectations are agreed according to your application and engagement." },
];

const DELIVERY_STAGES = workflowsteps.map((step, index) => ({ ...step, ...[{"title": "Requirements Discovery", "description": "We discuss your goals, users, current workflows and systems. This stage establishes the business requirements, constraints and questions that need to be resolved before planning."}, {"title": "Scope & Delivery Planning", "description": "We organize requirements into an agreed scope, map user journeys and outline the technical approach. Milestones, dependencies and responsibilities help establish a practical delivery plan."}, {"title": "Interface Design & Review", "description": "We develop wireframes, prototypes and interface designs for stakeholder review. Feedback helps refine the user experience and confirm key interactions before implementation."}, {"title": "Content & Asset Preparation", "description": "We prepare and organize the copy, images and other information the application needs. Content responsibilities and review points are coordinated with your team."}, {"title": "Development & Integration", "description": "We implement the agreed features, business logic and system integrations. Development follows the technical plan, with progress reviewed against project milestones."}, {"title": "Testing, Review & Launch", "description": "We validate functionality, usability and relevant performance and security requirements. Following issue resolution and agreed acceptance checks, we coordinate deployment and handover."}, {"title": "Maintenance & Improvement", "description": "After launch, we support the application through agreed maintenance activities, issue resolution and updates. Enhancements are prioritized around user feedback and business needs."}][index] }));

const NEEDS = ["Planning a new product", "Preparing an MVP", "Improving an existing application", "Adding integrations or AI", "Need help defining the scope"];
const NEXT_STEPS = [
  { n: "01", t: "Describe your goals and current stage", d: "Tell us what you want to achieve, what exists today and any important constraints." },
  { n: "02", t: "Review the delivery requirements", d: "We follow up to clarify features, dependencies and stakeholder expectations." },
  { n: "03", t: "Define a practical next step", d: "Discuss a discovery session, scoped proposal or focused first release that fits your priorities." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

// Framer Motion writes its own `transform`, which silently overrides any CSS
// `transform: rotate()` on the same element — so the engine panel's tilt has
// to be animated in, not declared in CSS.
const fadeUpRotate = {
  hidden: { opacity: 0, y: 18, rotate: 0 },
  show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      custom={i}
      variants={fadeUp}
      initial={reduceMotion ? false : "hidden"}
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

// Same section-for-section structure as /generative-ai-development, /about,
// and /our-core-team — plus the real seven-stage breakdown kept in full,
// since that list is this page's actual reason for existing.
const HowWeWork = () => {
  const navigate = useNavigate();
  const { setIsOpen, formData, setFormData } = useContext(GlobalStateContext);
  const [openFaq, setOpenFaq] = useState(-1);
  const [contactErrors, setContactErrors] = useState({});
  const [contactStatus, setContactStatus] = useState("idle");
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    document.title = "Our Software Development Process | InQmise Technologies";

    const description = document.createElement("meta");
    description.name = "description";
    description.content = "Explore InQmise’s software development process, from requirements and design to development, testing, launch and ongoing support.";
    document.head.appendChild(description);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://inqmisetechnologies.com/how-we-work-and-function");

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": { "@type": "Answer", "text": item.a },
      })),
    });
    document.head.appendChild(faqScript);
    return () => { description.remove(); faqScript.remove(); };
  }, []);

  const validateContactField = (name, value) => {
    switch (name) {
      case "fullname":
        return value.trim() ? "" : "Full name is required.";
      case "phone":
        if (!value.trim()) return "";
        return isValidPhone(value) ? "" : "Enter a valid phone number.";
      case "email":
        if (!value.trim()) return "Email is required.";
        return emailRegex.test(value.toLowerCase()) ? "" : "Enter a valid email address.";
      case "subject":
        return value.trim() ? "" : "Subject is required.";
      case "description":
        return value.trim() ? "" : "Tell us a little about your project.";
      default:
        return "";
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setContactErrors((prev) => ({ ...prev, [name]: validateContactField(name, value) }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    if (contactStatus === "submitting") return;
    const newErrors = {
      fullname: validateContactField("fullname", formData.fullname),
      phone: validateContactField("phone", formData.phone),
      email: validateContactField("email", formData.email),
      subject: validateContactField("subject", formData.subject),
      description: validateContactField("description", formData.description),
    };
    if (Object.values(newErrors).some(Boolean)) {
      setContactErrors(newErrors);
      const firstInvalid = Object.keys(newErrors).find((name) => newErrors[name]);
      document.getElementById(`hww-${firstInvalid}`)?.focus();
      return;
    }

    setContactStatus("submitting");
    submitLead(formData, "page")
      .then(() => {
        setContactStatus("success");
        setFormData({ fullname: "", phone: "", email: "", subject: "", description: "" });
        setContactErrors({});
      })
      .catch(() => {
        setContactStatus("error");
      });
  };

  const openContactModal = () => setIsOpen(true);

  return (
    <main className="editorial-ai pt-20">
      {/* HERO */}
      <header className="ea-hero ea-wrap">
        <Reveal as="div" reduceMotion={reduceMotion}>
          <p className="ea-eyebrow">How we work · Our delivery process</p>
          <h1>
            A clear process. <em>From idea to delivery.</em>
          </h1>
          <p className="ea-lede">
            Our software development process connects requirements, design, engineering and testing in a structured delivery plan. We tailor the activities to your project, with agreed milestones, stakeholder reviews and a plan for launch and ongoing support.
          </p>
          <div className="ea-actions">
            <button className="ea-button ea-primary" onClick={openContactModal} aria-label="Open contact form to start a project">
              Discuss your project ↗
            </button>
            <a className="ea-text-link" href="#stages">See the 7 stages</a>
          </div>
        </Reveal>

        <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
          <div className="ea-engine-top"><span /> INQMISE / DELIVERY PROCESS <b>7 STAGES</b></div>
          <h2>Clear requirements. Coordinated delivery.</h2>
          <div className="ea-query"><small>CONSISTENCY</small>A seven-stage framework, adapted to your scope, priorities and technical requirements.</div>
          <div className="ea-query"><small>ACCOUNTABILITY</small>Activities, responsibilities and review points are agreed with your team.</div>
          <div className="ea-flow"><b>Discover</b><i>→</i><b>Build</b><i>→</i><b>Launch</b></div>
        </Reveal>
      </header>

      <div className="hww-content">
      {/* TRUST LOGOS */}
      <div className="ea-wrap ea-logo-row">
        <span>BUSINESSES WE HAVE WORKED WITH</span>
        <div className="ea-logo-track">
          <div className="ea-logo-track-inner">
            {[...TRUST_LOGOS, ...TRUST_LOGOS].map((logo, i) => (
              <img key={`${logo.name}-${i}`} src={logo.src} alt={logo.name} loading="lazy" />
            ))}
          </div>
        </div>
      </div>

      {/* WHY THIS PROCESS WORKS — feature rows */}
      <section className="ea-wrap ea-section" id="collaboration">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">How we collaborate</div>
            <h2>Three principles for<br />effective delivery.</h2>
          </div>
          <p>A shared plan, timely feedback and clear responsibilities help teams move from requirements to a reviewed release.</p>
        </Reveal>

        <div className="hww-principles">
        {APPROACH.map((item, i) => (
          <Reveal as="article" i={i + 1} reduceMotion={reduceMotion} className="ea-feature" key={item.title}>
            <div className="ea-num">{item.num}</div>
            <h3>{item.title}</h3>
            <p>{item.copy}</p>
            <div className="ea-tags">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
          </Reveal>
        ))}
        </div>
      </section>

      {/* FOUR PHASE GROUPS — process grid */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">The four phases</div>
            <h2>Seven stages,<br />four phases.</h2>
          </div>
          <p>Four connected phases show how planning, implementation and ongoing support fit together.</p>
        </Reveal>
        <div className="ea-process">
          {PHASE_GROUPS.map((step, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-step" key={step.n}>
              <span>{step.n}</span>
              <i>↗</i>
              <h3>{step.t}</h3>
              <p>{step.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* THE FULL SEVEN STAGES */}
      <section className="ea-wrap ea-section hww-stages" id="stages">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">The seven stages</div>
            <h2>Our software development<br />process, step by step.</h2>
          </div>
          <p>Each stage has a defined purpose. Activities may overlap or be revisited as requirements and feedback develop.</p>
        </Reveal>
        <div className="re-numbered-list">
          {DELIVERY_STAGES.map((step, i) => {
            const Icon = step.icon;
            return (
              <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="re-numbered-row" key={step.number}>
                <span className="re-numbered-row-num">{step.number}</span>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                    <span className="ab-tile-icon" style={{ marginBottom: 0, color: step.color, fontSize: "var(--t-sm)" }}>
                      <Icon aria-hidden="true" />
                    </span>
                    <h3 style={{ margin: 0 }}>{step.title}</h3>
                  </div>
                  <p>{step.description}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <div className="hm-page">
      <section className="ea-wrap ea-section hm-section hww-cost" id="cost">
        <div className="ea-section-head hm-head">
          <div>
            <div className="ea-eyebrow">Cost and timeline</div>
            <h2>Know what you’re investing in.</h2>
          </div>
          <p>A useful estimate starts with your systems, data and goals. We assess scope, integrations and testing needs so you can weigh the expected value against development effort, operating costs and delivery risks.</p>
        </div>

        <div className="hm-cost-grid">
          <div>
            <h3>What determines your project budget</h3>
            <ol className="hm-drivers">
              {COST_DRIVERS.map((d) => (
                <li key={d.t}>
                  <b>{d.t}</b>
                  <p>{d.d}</p>
                </li>
              ))}
            </ol>
          </div>
          <div>
            <h3>How we plan delivery</h3>
            <dl className="hm-timeline">
              {TIMELINES.map((r) => (
                <div key={r.k}>
                  <dt>{r.k}</dt>
                  <dd>{r.v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        <div className="hm-estimate">
          <p><b>Choose the approach that fits the problem.</b> We assess whether conventional automation, custom software or AI is appropriate for your workflow, considering implementation effort, operating costs and maintenance.</p>
          <a className="ea-text-link" href="/agentic-ai-cost" onClick={(e) => { e.preventDefault(); navigate("/agentic-ai-cost"); }}>What drives AI project cost →</a>
        </div>
      </section>

      </div>

      {/* TOOLS BY STAGE — service cards */}
      <section className="ea-services">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">Technology selection</div>
              <h2>Tools chosen for<br />your project requirements.</h2>
            </div>
            <p>We select design, development and deployment tools based on your application, existing systems and long-term maintenance needs.</p>
          </Reveal>
          <div className="ea-cards">
            {TOOL_CARDS.map((item, i) => (
              <Reveal as="article" i={(i % 4) + 1} reduceMotion={reduceMotion} key={item.title} className={`ea-card ${item.featured ? "ea-featured" : ""}`}>
                <div className="ea-icon"><item.Icon aria-hidden="true" /></div>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY THIS PROCESS WORKS + FACTS */}
      <section className="ea-models">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">Delivery checkpoints</div>
              <h2>Keep requirements,<br />reviews and release aligned.</h2>
            </div>
            <p>Delivery checkpoints connect the agreed scope to stakeholder feedback, acceptance checks and operational readiness.</p>
          </Reveal>
          <div className="ea-model-layout">
            <Reveal as="div" className="ea-model-panel">
              <h3>What we review together</h3>
              {PROCESS_CRITERIA.map((row) => (
                <div key={row.a}><span>{row.a}</span><small>{row.b}</small></div>
              ))}
            </Reveal>
            <div className="ea-metrics">
              {FACTS.map((m, i) => (
                <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-metric" key={m.a}>
                  <strong>{m.a}</strong>
                  <span>{m.b}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PLANNED AROUND YOUR REQUIREMENTS — case-study-shaped spotlight */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Applied to your project</div>
            <h2>A shared framework.<br />A project-specific plan.</h2>
          </div>
          <p>The delivery framework provides structure, while the activities and technical checks reflect the type of work involved.</p>
        </Reveal>
        <Reveal as="div" className="ea-case">
          <div>
            <div className="ea-eyebrow">PLANNED AROUND YOUR REQUIREMENTS</div>
            <h3>Different projects need different priorities.</h3>
            <p>A web application needs browser and integration testing. A cloud engagement needs deployment and operational planning. An AI integration needs data preparation and output evaluation. We define the relevant activities before implementation begins.</p>
            <button className="ea-text-link" onClick={() => navigate("/services")}>See our services</button>
          </div>
          <div className="ea-results">
            {APPLIES_TO.map((r) => (
              <a key={r.a} href={r.path} onClick={(e) => { e.preventDefault(); navigate(r.path); }}><b>{r.a}</b><span>{r.b}</span><i aria-hidden="true">↗</i></a>
            ))}
          </div>
        </Reveal>
      </section>

      {/* WHERE IT APPLIES — industries grid */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Where it applies</div>
            <h2>How delivery adapts<br />to each service.</h2>
          </div>
          <p>Explore the practical considerations that shape planning, implementation and handover across our services.</p>
        </Reveal>
        <div className="ea-industries">
          {WHERE_IT_APPLIES.map((item, i) => (
            <Reveal as="div" i={(i % 4) + 1} reduceMotion={reduceMotion} className="ea-industry" key={item.a}>
              <b>{item.a}</b>
              <p>{item.b}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="ea-faq-section">
        <div className="ea-wrap ea-faq ea-section">
          <Reveal as="div" className="ea-section-head">
            <div className="ea-eyebrow">Common questions</div>
            <h2>Questions about how we work</h2>
            <p>Understand project stages, collaboration, timelines and post-launch support.</p>
          </Reveal>
          {FAQS.map((item, i) => {
            const isOpen = openFaq === i;
            const panelId = `hww-faq-panel-${i}`;
            const buttonId = `hww-faq-button-${i}`;
            return (
              <div className="ea-faq-row" key={item.q}>
                <h3 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>
                  <button
                    id={buttonId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  >
                    {item.q}<span>{isOpen ? "−" : "+"}</span>
                  </button>
                </h3>
                <p id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>{item.a}</p>
              </div>
            );
          })}
          <div style={{ textAlign: "center", marginTop: 20 }}>
            <a className="ea-text-link" href="/faq" onClick={(e) => { e.preventDefault(); navigate("/faq"); }}>See the full FAQ</a>
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <div className="hm-page hww-home-inquiry">
      <section className="ea-cta" aria-labelledby="how-we-work-cta">
        <div className="ea-wrap ea-cta-grid">
          <div className="hww-inquiry-copy">
            <div className="ea-eyebrow">Plan your delivery</div>
            <h2 id="how-we-work-cta">Give your next project a clear path to launch.</h2>
            <p>Tell us where your project stands and what you want to achieve. We will help identify the requirements, review points and delivery approach for your next release.</p>
            <ol className="hm-next">
              {NEXT_STEPS.map((s) => (
                <li key={s.n}>
                  <span className="hm-next-n">{s.n}</span>
                  <div>
                    <b>{s.t}</b>
                    <p>{s.d}</p>
                  </div>
                </li>
              ))}
            </ol>
            <div className="hm-direct">
              <a href={`tel:${company.phoneHref}`}>
                <span>Call</span>
                <b>{company.phone}</b>
              </a>
              <a href={`mailto:${emailaddress}`}>
                <span>Email</span>
                <b>{emailaddress}</b>
              </a>
            </div>
            <p className="hm-direct-note">{company.street}, {company.locality}, {company.region} {company.postalCode}</p>
          </div>

          <div className="ea-contact-card">
            <span>LET’S PLAN YOUR NEXT RELEASE</span>
            <h3>Plan your project with us.</h3>
            <p>Share your goals and project stage. We aim to follow up within one business day.</p>

            <fieldset className="hm-needs">
              <legend>Where are you in your project journey?</legend>
              <div className="hm-needs-row">
                {NEEDS.map((need) => (
                  <button
                    type="button"
                    key={need}
                    className={`hm-need${formData.subject === need ? " is-picked" : ""}`}
                    aria-pressed={formData.subject === need}
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, subject: need }));
                      setContactErrors((prev) => ({ ...prev, subject: "" }));
                    }}
                  >
                    {need}
                  </button>
                ))}
              </div>
            </fieldset>

            <form onSubmit={handleContactSubmit} aria-label="How we work project inquiry form" className="ea-form" noValidate>
              {contactStatus === "success" && (
                <p role="status" aria-live="polite" className="ea-form-msg success">
                  Thanks — we've received your details and will be in touch shortly.
                </p>
              )}
              {contactStatus === "error" && (
                <p role="alert" aria-live="assertive" className="ea-form-msg error">
                  Something went wrong submitting your request. Please try again.
                </p>
              )}

              <div className="ea-field">
                <label htmlFor="hww-fullname">Full Name</label>
                <input
                  id="hww-fullname" name="fullname" type="text" value={formData.fullname} onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.fullname)} aria-describedby={contactErrors.fullname ? "hww-fullname-error" : undefined}
                />
                {contactErrors.fullname && <p id="hww-fullname-error" className="ea-field-error">{contactErrors.fullname}</p>}
              </div>

              <div className="ea-form-row">
                <div className="ea-field">
                  <label htmlFor="hww-phone">Phone Number (optional)</label>
                  <input
                    id="hww-phone" name="phone" type="tel" value={formData.phone} onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.phone)} aria-describedby={contactErrors.phone ? "hww-phone-error" : undefined}
                  />
                  {contactErrors.phone && <p id="hww-phone-error" className="ea-field-error">{contactErrors.phone}</p>}
                </div>
                <div className="ea-field">
                  <label htmlFor="hww-email">Email</label>
                  <input
                    id="hww-email" name="email" type="email" value={formData.email} onChange={handleContactChange}
                    aria-invalid={Boolean(contactErrors.email)} aria-describedby={contactErrors.email ? "hww-email-error" : undefined}
                  />
                  {contactErrors.email && <p id="hww-email-error" className="ea-field-error">{contactErrors.email}</p>}
                </div>
              </div>

              <div className="ea-field">
                <label htmlFor="hww-subject">Subject</label>
                <input
                  id="hww-subject" name="subject" type="text" placeholder="Pick one above, or write your own"
                  value={formData.subject} onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.subject)} aria-describedby={contactErrors.subject ? "hww-subject-error" : undefined}
                />
                {contactErrors.subject && <p id="hww-subject-error" className="ea-field-error">{contactErrors.subject}</p>}
              </div>

              <div className="ea-field">
                <label htmlFor="hww-description">Tell us about your project</label>
                <textarea
                  id="hww-description" name="description" rows={3} value={formData.description} onChange={handleContactChange}
                  aria-invalid={Boolean(contactErrors.description)} aria-describedby={contactErrors.description ? "hww-description-error" : undefined}
                />
                {contactErrors.description && <p id="hww-description-error" className="ea-field-error">{contactErrors.description}</p>}
              </div>

              <button type="submit" disabled={contactStatus === "submitting"} className="ea-button ea-primary ea-submit">
                {contactStatus === "submitting" ? "Sending…" : "Discuss the delivery plan ↗"}
              </button>
              <p className="hm-form-note">Our team will review your goals and follow up about the next steps.</p>
            </form>
          </div>
        </div>
      </section>
      </div>
      </div>
    </main>
  );
};

export default HowWeWork;
